import React from 'react'
import { useSmartNavigate } from '../../router'

/**
 * useSmartLinkTo is a custom hook that returns a function to navigate to a given path.
 * It checks if the user is holding the Ctrl or Meta key (for Mac) or if the newTab parameter is set to true.
 * If so, it opens the link in a new tab. Otherwise, it uses the app's router to navigate.
 * @param newTab - A boolean indicating whether to always open the link in a new tab.
 * @returns A function that takes a path and returns a click event handler.
 */
export const useSmartLinkTo = () => {
  const navigate = useSmartNavigate()
  return React.useCallback(
    (path: string, newTab: boolean = false) => {
      return (event: React.MouseEvent<Element>) => {
        if (event.ctrlKey || event.metaKey || newTab) {
          window.open(path, '_blank')
        } else {
          navigate(path)
        }
      }
    },
    [navigate]
  )
}

interface SmartLinkOptionalProps {
  /** The URL to link to. If not provided, the component will render as a div. */
  to?: string
  /** Additional class names for styling. */
  className?: string
  /** The content to display inside the link or div. */
  children: React.ReactNode
}

/**
 * SmartLinkOptional component that conditionally renders a link or a div based on the value of the `to` prop.
 */
export const SmartLinkOptional = React.forwardRef<HTMLAnchorElement | HTMLDivElement, SmartLinkOptionalProps>(
  ({ to, className, children, ...props }, ref) => {
    return to ? (
      <SmartLink to={to} ref={ref as React.Ref<HTMLAnchorElement>} className={className} {...props}>
        {children}
      </SmartLink>
    ) : (
      <div className={className} ref={ref as React.Ref<HTMLDivElement>} {...props}>
        {children}
      </div>
    )
  }
)

/**
 * SmartLink component that renders a link that automatically decides if the routing should be handled by the app or the browser.
 * It uses the `useSmartLinkTo` hook to determine the appropriate routing method.
 * If a route is in the APP_ROUTES list in router.tsx, it will use the app router.
 * Otherwise, it will use the browser's default routing.
 */
export const SmartLink = React.forwardRef<
  HTMLAnchorElement,
  { to: string; children: React.ReactNode; className?: string }
>(({ to, children, className, ...props }, ref) => {
  const smartLink = useSmartLinkTo()

  return (
    <a
      {...props}
      className={className}
      href={to}
      ref={ref}
      onClick={(e: React.MouseEvent) => {
        e.preventDefault()
        smartLink(to)(e)
      }}
    >
      {children}
    </a>
  )
})
